import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Filter, X, Search } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import ProductCard from "../components/ProductCard";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopPage = ({ addToCart }) => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Build query params
        const params = new URLSearchParams();
        if (category) params.set("category", category);
        if (searchQuery) params.set("search", searchQuery);
        const queryString = params.toString() ? `?${params.toString()}` : "";

        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products${queryString}`),
          axios.get(`${API}/categories`),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (e) {
        console.error("Error fetching products:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [category, searchQuery]);

  const currentCategory = categories.find((c) => c.id === category);

  // Determine page title based on search or category
  const getPageTitle = () => {
    if (searchQuery) return `Search: "${searchQuery}"`;
    if (currentCategory) return currentCategory.name;
    return "All Products";
  };

  return (
    <div className="min-h-screen pt-20" data-testid="shop-page">
      {/* Header */}
      <section className="py-12 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {searchQuery && (
              <div className="flex items-center gap-2 text-primary mb-2">
                <Search className="w-5 h-5" />
                <span className="font-outfit text-sm uppercase tracking-wider">Search Results</span>
              </div>
            )}
            <h1 className="font-unbounded text-3xl md:text-4xl font-bold text-foreground mb-2">
              {getPageTitle()}
            </h1>
            <p className="font-outfit text-muted-foreground">
              {products.length} {products.length === 1 ? "product" : "products"} {searchQuery ? "found" : "available"}
            </p>
            {searchQuery && (
              <Link to="/shop" className="inline-block mt-3">
                <Badge variant="secondary" className="flex items-center gap-1 cursor-pointer hover:bg-muted">
                  Clear search
                  <X className="w-3 h-3" />
                </Badge>
              </Link>
            )}
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              <div>
                <h3 className="font-unbounded text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">
                  Categories
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/shop"
                    data-testid="filter-all"
                    className={`block font-outfit text-sm py-2 px-3 rounded-lg transition-colors ${
                      !category
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    All Products
                  </Link>
                  {categories.map((cat, idx) => (
                    <Link
                      key={`sidebar-${cat.franpos_id || cat.id || idx}-${cat.slug || ''}`}
                      to={`/shop/${cat.id}`}
                      data-testid={`filter-${cat.id}`}
                      className={`block font-outfit text-sm py-2 px-3 rounded-lg transition-colors ${
                        category === cat.id
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Mobile Filter Toggle */}
          <div className="lg:hidden flex items-center justify-between mb-4">
            <Button
              data-testid="mobile-filter-toggle"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-border"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            {category && (
              <Link to="/shop">
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1 cursor-pointer"
                >
                  {currentCategory?.name}
                  <X className="w-3 h-3" />
                </Badge>
              </Link>
            )}
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mb-6 p-4 rounded-xl bg-card border border-border"
            >
              <div className="flex flex-wrap gap-2">
                <Link to="/shop" onClick={() => setShowFilters(false)}>
                  <Badge
                    variant={!category ? "default" : "outline"}
                    className="cursor-pointer"
                  >
                    All
                  </Badge>
                </Link>
                {categories.map((cat, idx) => (
                  <Link
                    key={`mobile-filter-${cat.franpos_id || cat.id || idx}-${cat.slug || ''}`}
                    to={`/shop/${cat.id}`}
                    onClick={() => setShowFilters(false)}
                  >
                    <Badge
                      variant={category === cat.id ? "default" : "outline"}
                      className="cursor-pointer"
                    >
                      {cat.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-xl bg-muted animate-pulse"
                  />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20" data-testid="no-products-message">
                <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="font-outfit text-muted-foreground mb-4">
                  {searchQuery 
                    ? `No products found for "${searchQuery}"`
                    : "No products found in this category"}
                </p>
                <Button asChild variant="outline">
                  <Link to="/shop">View All Products</Link>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product, index) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    addToCart={addToCart}
                    index={index}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ShopPage;
